// src/pages/TermsForOrganizersPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiFileText } from "react-icons/fi";
import { usePageTitle } from "../hooks/usePageTitle";

const TermsForOrganizersPage: React.FC = () => {
	usePageTitle("主催者利用規約");

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mb-8">
					<Link
						to="/organizer/register"
						className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
					>
						<FiArrowLeft className="w-4 h-4 mr-1" />
						登録画面に戻る
					</Link>
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
						<div className="flex items-center gap-4 mb-4">
							<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full">
								<FiFileText className="w-6 h-6 text-white" />
							</div>
							<div>
								<h1 className="text-3xl font-bold text-gray-900 dark:text-white">
									主催者利用規約
								</h1>
								<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
									最終更新日: 2025年11月21日
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Terms Content */}
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
					<div className="prose prose-gray dark:prose-invert max-w-none">
						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第1条（総則）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								本規約は、合同会社blackadder（以下「当社」といいます）が提供するイベントチケット販売プラットフォーム「Blackadder」（以下「本サービス」といいます）における主催者向けサービスの利用条件を定めるものです。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								主催者として本サービスを利用される方（以下「主催者」といいます）は、本規約に同意の上、本サービスをご利用いただくものとします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第2条（定義）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								本規約において、次の各号に掲げる用語の意義は、当該各号に定めるとおりとします。
							</p>
							<ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>
									「主催者」:
									本サービスを利用してイベントの企画・運営・チケット販売を行う個人、アーティスト、法人または団体
								</li>
								<li>
									「購入者」: 主催者が販売するイベントチケットを購入するユーザー
								</li>
								<li>
									「イベント」:
									主催者が本サービスを通じて企画・開催する催し物、公演、セミナー等
								</li>
								<li>
									「チケット」: イベントへの入場権または参加権を表す電子チケット
								</li>
							</ol>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第3条（主催者登録）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								本サービスの主催者機能を利用するには、当社所定の方法により主催者登録を行い、当社の審査を通過する必要があります。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								主催者登録の際は、真実かつ正確な情報を提供するものとし、登録情報に変更が生じた場合は速やかに更新するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								当社は、以下のいずれかに該当する場合、主催者登録を承認しないことがあります。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>登録情報に虚偽、誤記または記載漏れがあった場合</li>
								<li>
									過去に本規約違反等により登録を取り消されたことがある場合
								</li>
								<li>
									反社会的勢力等に該当する、または関係を有すると当社が判断した場合
								</li>
								<li>その他、当社が主催者として不適当と判断した場合</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第4条（主催者の責任）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								主催者は、以下の事項について責任を負うものとします。
							</p>
							<ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>
									イベントの企画、運営、実施に関する一切の責任（安全管理、会場手配、必要な許認可の取得等を含む）
								</li>
								<li>イベント内容、チケット情報等の正確性および適法性</li>
								<li>購入者に対する適切な情報提供および問い合わせ対応</li>
								<li>
									イベント中止・延期・内容変更時の購入者への通知および適切な対応
								</li>
								<li>チケット転売対策および入場時の本人確認（必要に応じて）</li>
								<li>
									購入者の個人情報の適切な管理（当社から提供される情報を含む）
								</li>
							</ol>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第5条（チケット販売）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								主催者は、本サービスを通じてチケットを販売することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								主催者は、チケット販売にあたり、以下の事項を遵守するものとします。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>
									イベント内容、日時、会場、料金等の重要事項を正確に記載すること
								</li>
								<li>
									法令、公序良俗に反するイベントのチケットを販売しないこと
								</li>
								<li>チケット価格を不当に高額に設定しないこと</li>
								<li>チケット販売開始前に必要な情報を全て登録すること</li>
							</ul>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
								3.
								当社は、本規約に違反するチケット販売、または不適切と判断したイベントについて、事前の通知なく販売を停止または削除することができます。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第6条（手数料および決済）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								主催者は、本サービスの利用に関して、以下の手数料を当社に支払うものとします。
							</p>
							<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 my-4">
								<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
									<li>
										<strong>プラットフォーム利用料</strong>:
										チケット販売価格に対する所定の割合（主催者ごとに個別設定、標準は0%）
									</li>
									<li>
										<strong>決済手数料</strong>:
										チケット販売価格に対する3.6%（標準設定）
									</li>
								</ul>
								<p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
									※手数料率は主催者ごとに異なる場合があります。ダッシュボードで確認できます。
								</p>
							</div>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								チケット購入者が支払った代金は、当社が代理受領し、手数料を控除した金額を主催者に支払います。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3. 手数料は、チケット販売時点で発生し、返金時も返還されません。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第7条（売上金の入金）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								主催者は、本サービスのダッシュボードから売上金の入金申請を行うことができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								入金申請から主催者の指定口座への振込までは、通常3〜5営業日を要します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3. 振込手数料は当社が負担します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								4. 当社は、以下の場合、入金を保留または拒否することができます。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>登録された銀行口座情報に誤りがある場合</li>
								<li>本規約違反が疑われる場合</li>
								<li>
									イベントに関する購入者からの苦情・トラブルが発生している場合
								</li>
								<li>その他、当社が入金を不適切と判断した場合</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第8条（禁止事項）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								主催者は、以下の行為を行ってはなりません。
							</p>
							<ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>法令、公序良俗に反する行為</li>
								<li>虚偽または誤解を招く情報の掲載</li>
								<li>
									第三者の知的財産権、プライバシー権、その他の権利を侵害する行為
								</li>
								<li>未成年者の健全な育成を阻害する恐れのあるイベントの開催</li>
								<li>無許可での転売を目的としたチケット販売</li>
								<li>イベントを開催する意思がないのにチケットを販売する行為</li>
								<li>正当な理由なくイベントを中止・延期すること</li>
								<li>購入者に対する詐欺的行為</li>
								<li>本サービスのシステムに不正にアクセスする行為</li>
								<li>当社または第三者の信用を毀損する行為</li>
								<li>反社会的勢力への利益供与</li>
								<li>その他、当社が不適切と判断する行為</li>
							</ol>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第9条（キャンセル・返金）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								イベント中止・延期等により購入者への返金が必要となった場合、主催者は速やかに購入者への通知および返金対応を行うものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								返金は、当社システムを通じて行われます。返金に係る手数料は主催者が負担するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3. 返金時においても、当社が受領した手数料は返還されません。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第10条（知的財産権）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								主催者が本サービスに投稿・登録したコンテンツ（イベント情報、画像、説明文等）の知的財産権は主催者に帰属します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								主催者は、当社に対し、本サービスの提供、プロモーション、改善等の目的で、投稿コンテンツを無償で使用する権利を許諾するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3.
								本サービスに関する一切の知的財産権は当社または当社にライセンスを許諾している者に帰属します。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第11条（免責事項）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、本サービスが主催者の特定の目的に適合すること、期待する機能・商品的価値・正確性・有用性を有することを保証するものではありません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								主催者と購入者間のトラブルについて、当社は一切の責任を負いません。主催者は自己の責任において解決するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								システム障害、天災地変、その他不可抗力により本サービスが利用できない場合、当社は責任を負いません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								4.
								当社の責に帰すべき事由により主催者に損害が発生した場合でも、当社の賠償責任は、直接かつ通常の損害に限られ、その額は当該損害発生時の直近3ヶ月間に主催者が当社に支払った手数料の総額を上限とします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第12条（個人情報の取り扱い）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、主催者の個人情報を当社プライバシーポリシーに従って適切に取り扱います。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								主催者は、購入者の個人情報を本サービス利用目的の範囲内でのみ使用し、適切に管理するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3.
								主催者は、購入者の個人情報を第三者に提供または開示してはなりません。ただし、法令に基づく場合、または購入者の同意がある場合は除きます。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第13条（登録取消・利用停止）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、主催者が以下のいずれかに該当する場合、事前の通知なく主催者登録を取り消し、または本サービスの利用を停止することができます。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>本規約に違反した場合</li>
								<li>登録情報に虚偽があることが判明した場合</li>
								<li>6ヶ月以上本サービスの利用がない場合</li>
								<li>当社からの連絡に対し、相当期間内に応答がない場合</li>
								<li>その他、当社が主催者として不適当と判断した場合</li>
							</ul>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
								2.
								前項の措置により主催者に損害が生じた場合でも、当社は一切の責任を負いません。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第14条（規約の変更）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1. 当社は、必要に応じて本規約を変更することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								本規約を変更する場合、変更後の規約の効力発生日の14日前までに、本サービス上または当社ウェブサイトに掲載する方法により主催者に通知します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3.
								前項の通知後、主催者が本サービスの利用を継続した場合、変更後の規約に同意したものとみなします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第15条（準拠法および管轄裁判所）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1. 本規約の解釈および適用は、日本法に準拠するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								2.
								本サービスまたは本規約に関連して主催者と当社との間で紛争が生じた場合、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第16条（協議解決）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								本規約に定めのない事項または本規約の解釈に疑義が生じた場合、主催者と当社は誠実に協議の上解決するものとします。
							</p>
						</section>

						<div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
							<p className="text-sm text-gray-600 dark:text-gray-400 text-center">
								合同会社blackadder
							</p>
							<p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-1">
								制定日: 2025年11月21日
							</p>
						</div>
					</div>
				</div>

				{/* Back to Register Button */}
				<div className="mt-6 text-center">
					<Link
						to="/organizer/register"
						className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
					>
						<FiArrowLeft className="w-4 h-4 mr-2" />
						登録画面に戻る
					</Link>
				</div>
			</div>
		</div>
	);
};

export default TermsForOrganizersPage;
